import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, CircularProgress, IconButton } from "@mui/material";
import { FaArrowLeft, FaInstagram, FaRedo } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import _ from "lodash";
import { queryClient } from "queryClient";
import { getOrg } from "redux/selectors/getOrg";
import { useGetOnboarding } from "hooks/useGetOnboarding";
import { useGetChannelConnections } from "hooks/useGetChannelConnections";
import AgentsService from "services/admin/AgentsService";
import ChannelConnectionsAdminService from "services/admin/channelConnections";
import AgentSetupChat from "./AgentSetupChat";
import AgentDescribeStep from "./AgentDescribeStep";
import {
  AgentTone,
  Answers,
  BusinessKey,
  buildAgentPrompt,
  getBusinessTemplate,
  isAnswersComplete,
  parseAgentPrompt,
} from "./agentPromptTemplate";

const STEP_LABELS = ["Описание", "Проверка", "Включение"];
const CLOSE_PATH = "/admin/stats";

/**
 * Мастер «Настроить ИИ-агента» из «Быстрого старта»:
 * 1) пользователь выбирает тип бизнеса и отвечает на вопросы — промпт собираем по шаблону;
 * 2) проверяет агента в тестовом чате на частых вопросах;
 * 3) подключает агента к Instagram (по этому бэкенд отмечает шаг выполненным).
 */
const AgentSetup = () => {
  const navigate = useNavigate();
  const orgID = localStorage.getItem("orgID");
  const member: any = useSelector(getOrg);
  const orgName: string = _.get(member, "org.name", "");

  const { data: onboarding, isLoading: isOnboardingLoading } = useGetOnboarding(orgID);
  // Агент, уже подключённый к Instagram, важнее демо-агента
  const [createdAgentID, setCreatedAgentID] = useState<number | null>(null);
  const agentID = onboarding?.instagram?.agentID ?? onboarding?.agentID ?? createdAgentID;

  const { data: agent, isLoading: isAgentLoading } = useQuery({
    queryKey: ["ai-agent", agentID],
    enabled: !!agentID,
    queryFn: async () => (await AgentsService.get(agentID)).data,
    refetchOnWindowFocus: false,
  });

  const { data: agentChannels, refetch: refetchChannels } = useGetChannelConnections(agentID ? { agentID } : {});
  const sandbox = agentID
    ? (agentChannels || []).find((ch: any) => ch.source === "TEST_SANDBOX" && ch.agentID === agentID)
    : null;

  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState<Answers>({});
  const [tone, setTone] = useState<AgentTone>("formal");
  // Тип бизнеса: null — ещё не выбран
  const [businessKey, setBusinessKey] = useState<BusinessKey | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingSandbox, setIsCreatingSandbox] = useState(false);
  const [sandboxFailed, setSandboxFailed] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  // Новый тестовый чат — новый ключ
  const [chatKey, setChatKey] = useState(0);

  const parsed = parseAgentPrompt(agent?.prompt);

  // Заполняем ответы из промпта, если он уже собран мастером
  useEffect(() => {
    if (initialized || isOnboardingLoading || (agentID && isAgentLoading)) return;
    if (parsed.kind === "wizard") {
      setAnswers(parsed.answers);
      setTone(parsed.tone);
      setBusinessKey(parsed.businessKey);
    }
    setInitialized(true);
  }, [initialized, isOnboardingLoading, agentID, isAgentLoading, parsed]);

  // Тестовой среды может не быть, если демо-данные не создались — создаём её сами
  useEffect(() => {
    if (step !== 2 || !agentID || !agentChannels || sandbox || isCreatingSandbox || sandboxFailed) return;
    const createSandbox = async () => {
      try {
        setIsCreatingSandbox(true);
        const { data: created } = await ChannelConnectionsAdminService.post({ source: "TEST_SANDBOX", agentID });
        await ChannelConnectionsAdminService.connectTestSandbox(created.id);
        await refetchChannels();
      } catch (error) {
        console.error(error);
        setSandboxFailed(true);
      } finally {
        setIsCreatingSandbox(false);
      }
    };
    createSandbox();
  }, [step, agentID, agentChannels, sandbox, isCreatingSandbox, sandboxFailed, refetchChannels]);

  // Частые вопросы для тестового чата — под выбранный тип бизнеса
  const business = getBusinessTemplate(businessKey ?? "other");
  const hasSavedPrompt = !!agent && parsed.kind !== "empty";

  const savePrompt = async () => {
    if (!businessKey || !isAnswersComplete(businessKey, answers)) return;
    const prompt = buildAgentPrompt(orgName, answers, tone, businessKey);
    if (
      parsed.kind === "custom" &&
      !window.confirm("У агента уже есть промпт, написанный вручную. Заменить его вашими ответами?")
    ) {
      return;
    }
    try {
      setIsSaving(true);
      if (agentID) {
        // Промпт не менялся — сохранять нечего, чат продолжаем
        if (agent?.prompt !== prompt) {
          await AgentsService.put(agentID, { prompt });
          setChatKey((k) => k + 1);
        }
      } else {
        const { data: created }: any = await AgentsService.post({
          name: "ИИ-ассистент",
          status: "ACTIVE",
          llmModel: "gpt-4o-mini",
          timeZone: "Asia/Almaty",
          prompt,
        });
        setCreatedAgentID(created.id);
      }
      await queryClient.invalidateQueries({ queryKey: ["ai-agent"] });
      await queryClient.invalidateQueries({ queryKey: ["onboarding", orgID] });
      setStep(2);
    } catch (error) {
      console.error(error);
      toast.error("Не удалось сохранить описание");
    } finally {
      setIsSaving(false);
    }
  };

  const activateAgent = async () => {
    try {
      setIsActivating(true);
      await AgentsService.put(agentID, { status: "ACTIVE" });
      await queryClient.invalidateQueries({ queryKey: ["ai-agent", agentID] });
      toast.success("Агент включён");
    } catch (error) {
      toast.error("Не удалось включить агента");
    } finally {
      setIsActivating(false);
    }
  };

  const instagram = onboarding?.instagram;
  const connectToInstagram = async () => {
    if (!instagram || !agentID) return;
    try {
      setIsConnecting(true);
      await ChannelConnectionsAdminService.put(instagram.channelConnectionID, { agentID });
      if (agent?.status !== "ACTIVE") {
        await AgentsService.put(agentID, { status: "ACTIVE" });
      }
      await queryClient.invalidateQueries({ queryKey: ["onboarding", orgID] });
      toast.success("ИИ-агент подключён к Instagram");
      navigate(CLOSE_PATH);
    } catch (error) {
      console.error(error);
      toast.error("Не удалось подключить агента к Instagram");
    } finally {
      setIsConnecting(false);
    }
  };

  const goBack = () => (step === 1 ? navigate(CLOSE_PATH) : setStep(step - 1));

  const isLoading = !initialized;

  return (
    <div className="min-h-screen lg:h-screen w-full bg-white flex flex-col">
      <header className="h-16 shrink-0 border-b bg-white flex items-center gap-2 sm:gap-3 px-3 sm:px-6 sticky top-0 z-20">
        <IconButton onClick={goBack} size="small" aria-label="Назад">
          <FaArrowLeft size={18} />
        </IconButton>
        <img
          src="/media/images/ubee-logo.png"
          alt="Ubee AI"
          className="hidden sm:block w-8 h-8 rounded-lg object-contain"
        />
        <div className="font-bold text-base sm:text-lg truncate">Настройка ИИ-агента</div>
        <ol className="hidden md:flex items-center gap-2 mx-auto">
          {STEP_LABELS.map((label, index) => {
            const n = index + 1;
            const isActive = n === step;
            const isDone = n < step;
            return (
              <li key={label} className="flex items-center gap-2">
                {index > 0 && <span className="w-8 h-px bg-gray-200" />}
                {/* Вперёд — только кнопками внизу, чтобы описание успело сохраниться */}
                <button
                  type="button"
                  disabled={n > step}
                  onClick={() => setStep(n)}
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition-colors ${
                    isActive ? "bg-[#f3ecfa] text-[#6b2fa3] font-semibold" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                      isActive
                        ? "bg-[#6b2fa3] text-white"
                        : isDone
                          ? "bg-green-500 text-white"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {isDone ? "✓" : n}
                  </span>
                  {label}
                </button>
              </li>
            );
          })}
        </ol>
        <div className="ml-auto md:ml-0 text-sm text-gray-500 whitespace-nowrap md:hidden">Шаг {step} из 3</div>
        <IconButton onClick={() => navigate(CLOSE_PATH)} size="small" aria-label="Закрыть">
          <MdClose size={22} />
        </IconButton>
      </header>

      {isLoading ? (
        <div className="flex-grow flex items-center justify-center">
          <CircularProgress />
        </div>
      ) : (
        <main className="flex-grow lg:overflow-hidden">
          {step === 1 && (
            <AgentDescribeStep
              businessKey={businessKey}
              setBusinessKey={setBusinessKey}
              answers={answers}
              setAnswers={setAnswers}
              tone={tone}
              setTone={setTone}
              customPromptAgentID={parsed.kind === "custom" ? agentID : null}
              onSave={savePrompt}
              isSaving={isSaving}
            />
          )}

          {step === 2 && (
            <div className="h-full max-w-2xl mx-auto px-4 sm:px-8 py-6 flex flex-col gap-4">
              <div>
                <h2 className="text-2xl font-bold mb-1">Проверьте, как отвечает агент</h2>
                <p className="text-gray-500">
                  Спросите то, что обычно спрашивают ваши клиенты. Ответ неточный —{" "}
                  <button type="button" className="text-[#6b2fa3] underline" onClick={() => setStep(1)}>
                    дополните описание
                  </button>
                  .
                </p>
              </div>

              {agent && agent.status !== "ACTIVE" && (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" disabled={isActivating} onClick={activateAgent}>
                      Включить
                    </Button>
                  }
                >
                  Агент выключен и не будет отвечать даже в тесте.
                </Alert>
              )}

              <div className="flex-grow min-h-[420px]">
                {sandbox ? (
                  <AgentSetupChat key={chatKey} sandbox={sandbox} questions={business.questions} />
                ) : sandboxFailed ? (
                  <Alert severity="error">
                    Не удалось создать тестовую среду. Обновите страницу или проверьте агента в разделе{" "}
                    <Link to={`/admin/ai-agents/${agentID}/dialogs`} className="underline">
                      «Тестовая среда»
                    </Link>
                    .
                  </Alert>
                ) : (
                  <div className="h-full flex items-center justify-center border rounded-2xl">
                    <CircularProgress />
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setChatKey((k) => k + 1)}
                  startIcon={<FaRedo size={14} />}
                  sx={{ borderRadius: "12px", whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Новый чат
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!hasSavedPrompt}
                  onClick={() => setStep(3)}
                  sx={{ borderRadius: "12px" }}
                >
                  Всё хорошо, дальше
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="h-full overflow-y-auto">
              <div className="max-w-xl mx-auto px-4 sm:px-8 py-8">
                <h2 className="text-2xl font-bold mb-2">Включите агента в Instagram</h2>
                <p className="text-gray-500 mb-6">
                  Агент ответит в Директ на всё, что не подошло под сценарии чат-бота. Если менеджер сам ответил в чате,
                  агент в этом чате замолкает.
                </p>

                {!instagram?.connected ? (
                  <>
                    <Alert severity="info" className="mb-6">
                      Сначала подключите Instagram-аккаунт — после этого вернитесь сюда из «Быстрого старта».
                    </Alert>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<FaInstagram />}
                      onClick={() => navigate("/admin/connect/instagram?returnTo=onboarding")}
                      sx={{ borderRadius: "12px", py: 1.5 }}
                    >
                      Подключить Instagram
                    </Button>
                  </>
                ) : instagram.agentID === agentID && agent?.status === "ACTIVE" ? (
                  <>
                    <Alert severity="success" className="mb-6">
                      Агент уже отвечает в @{instagram.username}. Новое описание начнёт работать сразу.
                    </Alert>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => navigate(CLOSE_PATH)}
                      sx={{ borderRadius: "12px", py: 1.5 }}
                    >
                      Готово
                    </Button>
                  </>
                ) : (
                  <>
                    {instagram.agentID && instagram.agentID !== agentID && (
                      <Alert severity="warning" className="mb-4">
                        Сейчас в этом аккаунте отвечает другой ИИ-агент — он будет заменён.
                      </Alert>
                    )}
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<FaInstagram />}
                      disabled={isConnecting}
                      onClick={connectToInstagram}
                      sx={{ borderRadius: "12px", py: 1.5 }}
                    >
                      {isConnecting ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        `Включить в @${instagram.username || "Instagram"}`
                      )}
                    </Button>
                  </>
                )}

                <Button fullWidth color="inherit" className="!mt-3" onClick={() => navigate(CLOSE_PATH)}>
                  Позже
                </Button>
              </div>
            </div>
          )}
        </main>
      )}
    </div>
  );
};

export default AgentSetup;
